import React from 'react';
import { Box, Container, Typography, Stack, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { LpHeader, LpFooter, IsThisYou, LpCtaBlock, useScrollFade, fadeStyle, useLpTracking } from './LpShared';
import { trackEvent } from '../analytics';

const PAGE = '/lp/daycare-owner';

export default function LpDaycareOwner() {
  const navigate = useNavigate();
  useLpTracking(PAGE);
  const costSection = useScrollFade();
  const solveSection = useScrollFade();
  const proofSection = useScrollFade();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <LpHeader ctaLabel="Book a Demo" ctaPath="/contact" pagePath={PAGE} />

      {/* ── Emotional hero ── */}
      <Box sx={{ position: 'relative', minHeight: { xs: 520, md: 680 }, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <Box
          component="img"
          src="/daycare-woman-child.png"
          alt="Daycare caregiver with child"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.22 }}
        />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(10,10,18,0.97) 0%, rgba(10,10,18,0.75) 60%, rgba(10,10,18,0.88) 100%)' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 12 }, px: 3 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ff6b6b' }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#ff6b6b', letterSpacing: '0.1em', textTransform: 'uppercase' }}>For Daycare Owners</Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.4rem', md: '3.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', lineHeight: 1.05, mb: 3 }}>
            You built this.<br />
            <Box component="span" sx={{ color: '#ff6b6b' }}>One bad hire could take it all away.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 540, lineHeight: 1.65, mb: 5 }}>
            One incident. One lawsuit. Most daycare operators don't survive it financially or reputationally. SMCLA gives you the warning — before the incident happens, before the parents find out, before the lawyers get involved.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              size="large"
              onClick={() => { trackEvent('cta_click', 'Book a Demo', PAGE, { location: 'lp_hero' }); navigate('/contact'); }}
              sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.3)', '&:hover': { bgcolor: '#4ecfd4' } }}
            >
              Protect my facility — Book a Demo →
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ── Is this you? ── */}
      <Box sx={{ py: { xs: 7, md: 10 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <IsThisYou items={[
                "You can't be in every room at the same time — and you know it",
                "You trust your staff, but you've seen what one hire can do to a reputation",
                "You've read about the lawsuits and thought: what if that was me?",
                "Your insurance premium went up again — and you still can't prove you're safer",
              ]} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Box
                  component="img"
                  src="/brooklyn-daycare.png"
                  alt="Daycare facility"
                  sx={{ width: '100%', height: 280, objectFit: 'cover', display: 'block', opacity: 0.8 }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Cost of inaction ── */}
      <Box ref={costSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(costSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#ff6b6b', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>The cost of inaction</Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 5 }}>
            This is what happens when you're not watching.
          </Typography>
          <Grid container spacing={3}>
            {[
              { num: '$262K', label: 'Average physical abuse settlement', sub: 'North Carolina, 2024. That\'s a real number, not a headline.', color: '#ff6b6b' },
              { num: '$16M', label: 'Single death settlement', sub: 'South Carolina, 2024. One facility. One incident. One year.', color: '#ff9f43' },
              { num: '18 mo.', label: 'Typical time to closure', sub: 'Most operators cannot absorb a major claim. They close.', color: '#8c52ff' },
            ].map(({ num, label, sub, color }) => (
              <Grid key={num} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: `1px solid ${color}20`, borderRadius: 3, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.2rem', color, lineHeight: 1, mb: 1 }}>{num}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{sub}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── How SMCLA solves it ── */}
      <Box ref={solveSection.ref} sx={{ py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(solveSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>What SMCLA gives you</Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 5 }}>
            You know before the parents do.<br />Before the lawyers do.
          </Typography>
          <Stack spacing={2.5}>
            {[
              { num: '1', title: 'Alert in under 60 seconds', body: 'The moment SMCLA detects a behavioral event — grabbing, prolonged distress, unsupervised children — you get a push notification. Not the next day. Not when a parent complains. Within a minute.' },
              { num: '2', title: 'Tamper-evident clips protect your staff and your facility', body: 'When something goes wrong, you have timestamped, chain-of-custody footage that shows exactly what happened. It protects innocent staff from false accusations — and provides legal-grade evidence when needed.' },
              { num: '3', title: 'Your insurance provider can finally verify your monitoring', body: 'SMCLA gives you documented, verified monitoring data — the kind that justifies premium discounts and proves to underwriters that your facility is actively managed, not passively surveilled.' },
            ].map(({ num, title, body }) => (
              <Box key={num} sx={{ display: 'flex', gap: 3, p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, alignItems: 'flex-start', '&:hover': { borderColor: 'rgba(92,225,230,0.25)' }, transition: 'border-color 0.2s' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 16, color: '#5ce1e6' }}>{num}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 1 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── Video proof ── */}
      <Box ref={proofSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(proofSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5, textAlign: 'center' }}>Real footage. Real detection.</Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 4, textAlign: 'center' }}>
            See what SMCLA catches — and what it correctly ignores.
          </Typography>
          <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(92,225,230,0.15)', mb: 3 }}>
            <video src="/Daycare_Video_with_AI_Summary.mp4" controls muted playsInline style={{ width: '100%', maxHeight: 400, objectFit: 'cover', display: 'block' }}>
              Your browser does not support embedded videos.
            </video>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Button variant="text" onClick={() => { trackEvent('cta_click', 'See more examples', PAGE); navigate('/examples'); }} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#5ce1e6', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
              See more real detection examples →
            </Button>
          </Box>
        </Container>
      </Box>

      <LpCtaBlock
        headline="30 minutes. See it protect your facility."
        sub="Book a personalized walkthrough. We'll show you live detection, the alert workflow, and how other daycare owners use SMCLA to sleep better at night."
        ctaLabel="Book a Free Demo"
        ctaPath="/contact"
        pagePath={PAGE}
      />
      <LpFooter />
    </Box>
  );
}
